import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useVideoPlayer } from 'design-system';
import {
  setLearningDocumentScroll,
  setLearningGuidanceIndex,
} from '../../redux/learning/actions';
import {
  selectLearningGuidance,
  selectLearningPackageId,
} from '../../redux/learning/selectors';
import { documentSeenPercent } from '../helpers/documentScroll';
import {
  readVideoResume,
  videoResumeStorageKey,
  writeVideoResume,
} from '../helpers/videoResumeStorage';

/** Guidance flow state for video + document steps. */
export function useGuidanceFlow({ course, onCompleteGuidance } = {}) {
  const dispatch = useDispatch();
  const guidance = useSelector(selectLearningGuidance);
  const {
    index: guidanceIndex,
    documentReadPercent,
    documentComplete,
    documentNeedsScroll,
  } = guidance;

  const sessionPackageId = useSelector(selectLearningPackageId);
  const learnerId = useSelector(
    (state) => state.learning.inviteId || state.learning.userId || '',
  );

  const guidanceSteps = course?.guidanceSteps ?? [];
  const currentGuidance = guidanceSteps[guidanceIndex];
  const requireFullCompletion = currentGuidance?.require_full_completion ?? true;
  const videoStorageKey = currentGuidance?.type === 'video'
    ? videoResumeStorageKey({
      packageId: course?.packageId || sessionPackageId,
      guidanceId: currentGuidance.id,
      learnerId,
    })
    : null;

  const videoPlayer = useVideoPlayer({
    resetKey: guidanceIndex,
    preventSkipAhead: requireFullCompletion,
    storageKey: videoStorageKey,
    readProgress: readVideoResume,
    writeProgress: writeVideoResume,
  });

  const documentCompleteLatchedRef = useRef(false);
  // Peak lives in a ref, not in the dispatched state: several measurements can
  // run before a re-render, and they would all read the same stale percent.
  const maxReadPercentRef = useRef(0);

  useEffect(() => {
    documentCompleteLatchedRef.current = documentComplete;
    maxReadPercentRef.current = documentReadPercent;
  }, [course?.packageId, guidanceIndex]);

  const updateDocumentScrollState = (element) => {
    if (!element) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = element;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    // How much of the document has been on screen. A taller reader shows more
    // of it, so maximizing can raise this — but only the last line being
    // visible completes the step, whatever the reader size.
    const clamped = documentSeenPercent({ scrollTop, scrollHeight, clientHeight });
    const reachedEnd = distanceFromBottom <= 4 || clamped >= 99;

    if (reachedEnd) {
      documentCompleteLatchedRef.current = true;
    }

    const latchedComplete = documentCompleteLatchedRef.current;
    // Percent is viewport-derived, so minimizing would otherwise move it
    // backwards. Keep the furthest point reached, like `maxWatched`.
    maxReadPercentRef.current = latchedComplete
      ? 100
      : Math.max(maxReadPercentRef.current, clamped);

    const resolvedState = {
      documentNeedsScroll: scrollHeight > clientHeight,
      documentReadPercent: maxReadPercentRef.current,
      documentComplete: latchedComplete,
    };

    if (
      documentNeedsScroll === resolvedState.documentNeedsScroll
      && documentReadPercent === resolvedState.documentReadPercent
      && documentComplete === resolvedState.documentComplete
    ) {
      return;
    }

    dispatch(setLearningDocumentScroll(resolvedState));
  };

  const handleDocumentScroll = (event) => {
    updateDocumentScrollState(event.currentTarget);
  };

  const measureDocumentScroll = (element) => {
    updateDocumentScrollState(element);
  };

  const stepComplete =
    currentGuidance?.type === 'video' ? videoPlayer.complete : documentComplete;

  const sidebarSteps = useMemo(() => {
    return (course?.progressSteps ?? []).map((step) => {
      if (step.locked) {
        return { ...step, status: 'locked' };
      }

      const guidanceIdx = guidanceSteps.findIndex((g) => g.progressStepId === step.id);
      if (guidanceIdx === -1) {
        return { ...step, status: 'pending' };
      }
      if (guidanceIdx < guidanceIndex) {
        return { ...step, status: 'completed' };
      }
      if (guidanceIdx === guidanceIndex) {
        return { ...step, status: 'active', contentComplete: stepComplete };
      }
      return { ...step, status: 'pending' };
    });
  }, [course?.progressSteps, guidanceIndex, guidanceSteps, stepComplete]);

  const continueGuidance = () => {
    if (guidanceIndex < guidanceSteps.length - 1) {
      dispatch(setLearningGuidanceIndex(guidanceIndex + 1));
      return;
    }
    onCompleteGuidance?.();
  };

  const canContinue = requireFullCompletion ? stepComplete : true;

  return {
    guidanceIndex,
    currentGuidance,
    sidebarSteps,
    videoPlayer,
    documentReadPercent,
    documentComplete,
    documentNeedsScroll,
    handleDocumentScroll,
    measureDocumentScroll,
    continueGuidance,
    canContinue,
  };
}

export { formatVideoTime } from 'design-system';
